import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Phone,
  MapPin,
  ClipboardList,
  Stethoscope,
  Loader2,
  Tag,
  Briefcase,
  Globe,
  FolderOpen,
  FileText,
  Info,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { CustomFieldGroupsTab, CustomFieldsTab, MoreInfoPanel, SpecialistFormPanel } from '../components/specialists';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Common country calling codes for Latin America and international
const countryCodes = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
];

const SpecialistForm = ({ specialist, specialistTypes, availableServices, countries, cities, onSubmit, onClose, t }) => {
  const [formData, setFormData] = useState(specialist || {
    first_name: '',
    last_name: '',
    document_type: 'CC',
    document_number: '',
    email: '',
    phone_country_code: '+57',
    phone: '',
    whatsapp_username: '',
    specialist_type: '',
    specialties: [],
    license_number: '',
    assigned_services: [],
    service_cities: [],
    service_country_id: '',
    max_daily_services: 8,
    status: 'active',
    custom_field_values: []
  });
  const [newSpecialty, setNewSpecialty] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

  // Initialize custom_field_values from specialist if editing
  useEffect(() => {
    if (specialist && specialist.custom_field_values) {
      setFormData(prev => ({
        ...prev,
        custom_field_values: specialist.custom_field_values || []
      }));
    }
  }, [specialist]);

  // Get selected type details
  const selectedType = specialistTypes.find(t => t.code === formData.specialist_type);

  // Filter cities by selected country using useMemo to avoid infinite loops
  const filteredCities = useMemo(() => {
    if (formData.service_country_id) {
      return cities.filter(c => c.country_id === formData.service_country_id && c.status === 'active');
    }
    return cities.filter(c => c.status === 'active');
  }, [formData.service_country_id, cities]);

  const handleCountryChange = (countryId) => {
    setFormData({
      ...formData,
      service_country_id: countryId,
      service_cities: [] // Reset cities when country changes
    });
  };

  const toggleCity = (cityName) => {
    const currentCities = formData.service_cities || [];
    if (currentCities.includes(cityName)) {
      setFormData({
        ...formData,
        service_cities: currentCities.filter(c => c !== cityName)
      });
    } else {
      setFormData({
        ...formData,
        service_cities: [...currentCities, cityName]
      });
    }
  };

  const addSpecialty = () => {
    if (newSpecialty) {
      setFormData({
        ...formData, 
        specialties: [...formData.specialties, { name: newSpecialty }]
      });
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (idx) => {
    const newSpecs = formData.specialties.filter((_, i) => i !== idx);
    setFormData({...formData, specialties: newSpecs});
  };

  const toggleService = (service) => {
    const currentServices = formData.assigned_services || [];
    const exists = currentServices.some(s => s.service_id === service.id);
    
    if (exists) {
      setFormData({
        ...formData,
        assigned_services: currentServices.filter(s => s.service_id !== service.id)
      });
    } else {
      setFormData({
        ...formData,
        assigned_services: [...currentServices, {
          service_id: service.id,
          service_name: service.name,
          service_code: service.code
        }]
      });
    }
  };

  const isServiceSelected = (serviceId) => {
    return (formData.assigned_services || []).some(s => s.service_id === serviceId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving specialist:', error);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">{t('form.firstName')} *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            required
            data-testid="specialist-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{t('form.lastName')} *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            required
            data-testid="specialist-last-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="document_type">{t('form.documentType')} *</Label>
          <Select
            value={formData.document_type}
            onValueChange={(value) => setFormData({...formData, document_type: value})}
          >
            <SelectTrigger data-testid="specialist-doc-type">
              <SelectValue placeholder={t('common:select')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CC">{t('documentTypes.CC')}</SelectItem>
              <SelectItem value="CE">{t('documentTypes.CE')}</SelectItem>
              <SelectItem value="PA">{t('documentTypes.PA')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="document_number">{t('form.documentNumber')} *</Label>
          <Input
            id="document_number"
            value={formData.document_number}
            onChange={(e) => setFormData({...formData, document_number: e.target.value})}
            required
            data-testid="specialist-doc-number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('form.email')} *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            required
            data-testid="specialist-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">{t('form.phone')} *</Label>
          <div className="flex gap-2">
            <Select
              value={formData.phone_country_code || '+57'}
              onValueChange={(value) => setFormData({...formData, phone_country_code: value})}
            >
              <SelectTrigger className="w-[120px]" data-testid="specialist-phone-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="3001234567"
              required
              className="flex-1"
              data-testid="specialist-phone"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsapp_username">{t('form.whatsapp')}</Label>
        <Input
          id="whatsapp_username"
          value={formData.whatsapp_username || ''}
          onChange={(e) => setFormData({...formData, whatsapp_username: e.target.value})}
          placeholder={t('form.whatsappPlaceholder')}
          data-testid="specialist-whatsapp"
        />
        <p className="text-xs text-muted-foreground">{t('form.whatsappHint')}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="specialist_type">{t('form.specialistType')} *</Label>
          <Select
            value={formData.specialist_type}
            onValueChange={(value) => setFormData({...formData, specialist_type: value})}
          >
            <SelectTrigger data-testid="specialist-type-select">
              <SelectValue placeholder={t('form.selectType')} />
            </SelectTrigger>
            <SelectContent>
              {specialistTypes.length === 0 ? (
                <SelectItem value="__no_types__" disabled>{t('form.noTypesConfigured')}</SelectItem>
              ) : (
                specialistTypes.filter(t => t.status === 'active').map((type) => (
                  <SelectItem key={type.id} value={type.code}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: type.color }}
                      />
                      {type.name}
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {selectedType && (
            <p className="text-xs text-muted-foreground mt-1">
              {selectedType.description}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="license_number">
            {t('form.licenseNumber')} {selectedType?.requires_license && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id="license_number"
            value={formData.license_number}
            onChange={(e) => setFormData({...formData, license_number: e.target.value})}
            required={selectedType?.requires_license}
            data-testid="specialist-license"
          />
          {selectedType && !selectedType.requires_license && (
            <p className="text-xs text-muted-foreground">{t('form.licenseNotRequired')}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="max_daily_services">{t('form.maxDailyServices')}</Label>
          <Input
            id="max_daily_services"
            type="number"
            min="1"
            max="20"
            value={formData.max_daily_services}
            onChange={(e) => setFormData({...formData, max_daily_services: parseInt(e.target.value)})}
            data-testid="specialist-max-services"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">{t('form.status')}</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({...formData, status: value})}
          >
            <SelectTrigger data-testid="specialist-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t('status.active')}</SelectItem>
              <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
              <SelectItem value="on_leave">{t('status.on_leave')}</SelectItem>
              <SelectItem value="busy">{t('status.busy')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('form.serviceCountry')}</Label>
        <Select
          value={formData.service_country_id || ''}
          onValueChange={handleCountryChange}
        >
          <SelectTrigger data-testid="specialist-country">
            <SelectValue placeholder={t('form.selectCountry')} />
          </SelectTrigger>
          <SelectContent>
            {countries.filter(c => c.status === 'active').map((country) => (
              <SelectItem key={country.id} value={country.id}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>{t('form.serviceCities')}</Label>
        <div className="border rounded-lg p-3 max-h-40 overflow-y-auto bg-muted">
          {!formData.service_country_id ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('form.selectCountryFirst')}
            </p>
          ) : filteredCities.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('form.noCitiesConfigured')}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredCities.map((city) => (
                <label
                  key={city.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    formData.service_cities?.includes(city.name) 
                      ? 'bg-[#FDE8EA] border border-[#E63946] dark:bg-[#E63946]/20 dark:border-[#E63946]' 
                      : 'bg-background border border-border hover:bg-muted'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.service_cities?.includes(city.name) || false}
                    onChange={() => toggleCity(city.name)}
                    className="h-4 w-4 rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]"
                  />
                  <div className="flex items-center gap-1 text-sm text-foreground">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    {city.name}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        {formData.service_cities?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.service_cities.map((city, idx) => (
              <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => toggleCity(city)}>
                <MapPin className="h-3 w-3 mr-1" />
                {city} ×
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t('form.specialties')}</Label>
        <div className="flex gap-2">
          <Input
            value={newSpecialty}
            onChange={(e) => setNewSpecialty(e.target.value)}
            placeholder={t('form.addSpecialty')}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
          />
          <Button type="button" variant="outline" onClick={addSpecialty}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.specialties.map((spec, idx) => (
            <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => removeSpecialty(idx)}>
              {spec.name} ×
            </Badge>
          ))}
        </div>
      </div>

      {/* Assigned Services Section */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Briefcase className="h-4 w-4" />
          {t('form.assignedServices')}
        </Label>
        <div className="border rounded-lg p-3 max-h-48 overflow-y-auto bg-muted">
          {availableServices.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-2">
              {t('form.noServicesConfigured')}
            </p>
          ) : (
            <div className="space-y-2">
              {availableServices.filter(s => s.status === 'active').map((service) => (
                <label
                  key={service.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${
                    isServiceSelected(service.id) ? 'bg-[#FDE8EA] border border-[#E63946] dark:bg-[#E63946]/20 dark:border-[#E63946]' : 'bg-background border border-border hover:bg-muted'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isServiceSelected(service.id)}
                    onChange={() => toggleService(service)}
                    className="h-4 w-4 rounded border-gray-300 text-[#E63946] focus:ring-[#E63946]"
                    data-testid={`service-checkbox-${service.id}`}
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-foreground">{service.name}</div>
                    <div className="text-xs text-muted-foreground">{service.code} - ${service.base_price?.toLocaleString() || 0}</div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        {(formData.assigned_services || []).length > 0 && (
          <p className="text-xs text-muted-foreground">
            {formData.assigned_services.length} {t('form.servicesSelected')}
          </p>
        )}
      </div>

      {/* More Information Button */}
      <div className="border-t pt-4">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setIsMoreInfoOpen(true)}
        >
          <Info className="h-4 w-4 mr-2" />
          {t('form.moreInformation') || 'More Information'}
          {formData.custom_field_values?.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {formData.custom_field_values.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* More Information Panel */}
      <MoreInfoPanel
        isOpen={isMoreInfoOpen}
        onClose={() => setIsMoreInfoOpen(false)}
        customFieldValues={formData.custom_field_values || []}
        onValuesChange={(values) => setFormData({ ...formData, custom_field_values: values })}
        t={t}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button 
          type="submit" 
          className="bg-[#E63946] hover:bg-[#C92A36]" 
          disabled={loading || !formData.specialist_type}
          data-testid="save-specialist-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {specialist ? t('form.update') : t('form.save')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const SpecialistsPage = () => {
  const { t } = useTranslation('specialists');
  const [specialists, setSpecialists] = useState([]);
  const [specialistTypes, setSpecialistTypes] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  
  // Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchSpecialistTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/specialist-types`);
      setSpecialistTypes(response.data);
    } catch (error) {
      console.error('Error fetching specialist types:', error);
    }
  }, []);

  const fetchAvailableServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`);
      setAvailableServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  const fetchCities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, []);

  const fetchSpecialists = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (typeFilter && typeFilter !== 'all') params.append('specialist_type', typeFilter);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/specialists?${params.toString()}`);
      setSpecialists(response.data);
    } catch (error) {
      console.error('Error fetching specialists:', error);
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, statusFilter, t]);

  useEffect(() => {
    fetchSpecialistTypes();
    fetchAvailableServices();
    fetchCountries();
    fetchCities();
  }, [fetchSpecialistTypes, fetchAvailableServices, fetchCountries, fetchCities]);

  useEffect(() => {
    fetchSpecialists();
  }, [fetchSpecialists]);

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/specialists`, data);
      toast.success(t('messages.created'));
      fetchSpecialists();
      fetchSpecialistTypes(); // Refresh counts
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorCreating'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/specialists/${selectedSpecialist.id}`, data);
      toast.success(t('messages.updated'));
      fetchSpecialists();
      fetchSpecialistTypes(); // Refresh counts
    } catch (error) {
      const message = error.response?.data?.detail;
      toast.error(typeof message === 'string' ? message : t('messages.errorUpdating'));
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/specialists/${id}`);
      toast.success(t('messages.deleted'));
      fetchSpecialists();
      fetchSpecialistTypes(); // Refresh counts
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeleting'));
    }
  };

  const openEdit = async (specialistId) => {
    try {
      const response = await axios.get(`${API_URL}/api/specialists/${specialistId}`);
      setSelectedSpecialist(response.data);
      setIsFormOpen(true);
    } catch (error) {
      toast.error(t('messages.errorLoadingDetails'));
    }
  };

  const openView = async (specialistId) => {
    try {
      const response = await axios.get(`${API_URL}/api/specialists/${specialistId}`);
      setSelectedSpecialist(response.data);
      setIsViewOpen(true);
    } catch (error) {
      toast.error(t('messages.errorLoadingDetails'));
    }
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/specialists/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'specialists_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.templateDownloaded') || 'Template downloaded');
    } catch (error) {
      toast.error(t('importExport.errorDownloadingTemplate') || 'Error downloading template');
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/specialists/export/data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `specialists_export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.exportSuccess', { count: specialists.length }) || `${specialists.length} specialists exported`);
    } catch (error) {
      toast.error(t('importExport.errorExporting') || 'Error exporting specialists');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error(t('importExport.onlyCsv') || 'Only CSV files are allowed');
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API_URL}/api/specialists/import?skip_duplicates=true`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(t('importExport.importSuccess', { count: response.data.imported }) || `${response.data.imported} specialists imported`);
        fetchSpecialists();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('importExport.errorImporting') || 'Error importing specialists');
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Get type name and color for display
  const getTypeInfo = (typeCode) => {
    const type = specialistTypes.find(t => t.code === typeCode);
    return type || { name: typeCode, color: '#6B7280' };
  };

  return (
    <div className="space-y-6" data-testid="specialists-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Work_Sans',sans-serif]">
            {t('title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="list" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="list" className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            {t('tabs.list') || 'Specialists'}
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            {t('tabs.groups') || 'Groups'}
          </TabsTrigger>
          <TabsTrigger value="customFields" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('tabs.customFields') || 'Custom Fields'}
          </TabsTrigger>
        </TabsList>

        {/* Specialists List Tab */}
        <TabsContent value="list" className="space-y-4 mt-4">
          {/* Add Specialist Button */}
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline"
              onClick={() => setIsImportOpen(true)}
              data-testid="import-specialists-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('importExport.import') || 'Import'}
            </Button>
            <Button 
              variant="outline"
              onClick={handleExport}
              disabled={isExporting || specialists.length === 0}
              data-testid="export-specialists-btn"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('importExport.export') || 'Export'}
            </Button>
            <Button 
              className="bg-[#E63946] hover:bg-[#C92A36]"
              onClick={() => { setSelectedSpecialist(null); setIsFormOpen(true); }}
              data-testid="add-specialist-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newSpecialist')}
            </Button>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="specialist-search"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="specialist-type-filter">
                    <SelectValue placeholder={t('common:type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTypes')}</SelectItem>
                    {specialistTypes.map((type) => (
                      <SelectItem key={type.id} value={type.code}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: type.color }}
                          />
                          {type.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="specialist-status-filter">
                    <SelectValue placeholder={t('common:status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('status.all')}</SelectItem>
                    <SelectItem value="active">{t('status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                    <SelectItem value="on_leave">{t('status.on_leave')}</SelectItem>
                <SelectItem value="busy">{t('status.busy')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Specialists Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border" data-testid="specialists-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.specialist')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.type')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.services')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.contact')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.cities')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.status')}</th>
                    <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.orders')}</th>
                    <th className="px-4 py-3 bg-muted text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-border">
                  {specialists.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">
                        {t('table.noSpecialists')}
                      </td>
                    </tr>
                  ) : (
                    specialists.map((spec) => {
                      const typeInfo = getTypeInfo(spec.specialist_type);
                      return (
                        <tr key={spec.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div 
                                className="h-10 w-10 rounded-full flex items-center justify-center text-white"
                                style={{ backgroundColor: typeInfo.color }}
                              >
                                <Stethoscope className="h-5 w-5" />
                              </div>
                              <div>
                                <div className="font-medium text-foreground">
                                  {spec.first_name} {spec.last_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Tag className="h-4 w-4" style={{ color: typeInfo.color }} />
                              <span className="text-sm text-muted-foreground">{typeInfo.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {spec.assigned_services?.slice(0, 2).map((svc, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                                  <Briefcase className="h-3 w-3 mr-1" />
                                  {svc}
                                </Badge>
                              ))}
                              {spec.assigned_services?.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{spec.assigned_services.length - 2}
                                </Badge>
                              )}
                              {(!spec.assigned_services || spec.assigned_services.length === 0) && (
                                <span className="text-xs text-muted-foreground">{t('table.noServices')}</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {spec.phone}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {spec.service_cities?.slice(0, 2).map((city, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {city}
                                </Badge>
                              ))}
                              {spec.service_cities?.length > 2 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{spec.service_cities.length - 2}
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${
                              spec.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                              spec.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                              spec.status === 'on_leave' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                              'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
                            }`}>
                              {t(`status.${spec.status}`)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline">
                              <ClipboardList className="h-3 w-3 mr-1" />
                              {spec.active_orders_count || 0}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openView(spec.id)}
                                data-testid={`view-specialist-${spec.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(spec.id)}
                                data-testid={`edit-specialist-${spec.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDelete(spec.id)}
                                data-testid={`delete-specialist-${spec.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                {t('tabs.groups') || 'Field Groups'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CustomFieldGroupsTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="customFields" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('tabs.customFields') || 'Custom Fields'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CustomFieldsTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Specialist Form Panel (Right Side Sheet) */}
      <SpecialistFormPanel
        specialist={selectedSpecialist}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedSpecialist(null);
        }}
        onSubmit={selectedSpecialist ? handleUpdate : handleCreate}
        specialistTypes={specialistTypes}
        cities={cities}
      />

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('detail.title')}</DialogTitle>
          </DialogHeader>
          {selectedSpecialist && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div 
                  className="h-16 w-16 rounded-full flex items-center justify-center text-white text-xl"
                  style={{ backgroundColor: getTypeInfo(selectedSpecialist.specialist_type).color }}
                >
                  {selectedSpecialist.first_name?.[0]}{selectedSpecialist.last_name?.[0]}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {selectedSpecialist.first_name} {selectedSpecialist.last_name}
                  </h3>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Tag className="h-4 w-4" style={{ color: getTypeInfo(selectedSpecialist.specialist_type).color }} />
                    {getTypeInfo(selectedSpecialist.specialist_type).name}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('detail.document')}</Label>
                  <p className="font-medium text-foreground">
                    {selectedSpecialist.document_type} {selectedSpecialist.document_number}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.license')}</Label>
                  <p className="font-medium text-foreground">{selectedSpecialist.license_number || t('detail.notRegistered')}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.email')}</Label>
                  <p className="font-medium text-foreground">{selectedSpecialist.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.phone')}</Label>
                  <p className="font-medium text-foreground">{selectedSpecialist.phone}</p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">{t('detail.specialties')}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedSpecialist.specialties?.map((spec, idx) => (
                    <Badge key={idx} variant="secondary">{spec.name || spec}</Badge>
                  ))}
                  {(!selectedSpecialist.specialties || selectedSpecialist.specialties.length === 0) && (
                    <span className="text-muted-foreground text-sm">{t('detail.noSpecialties')}</span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  {t('detail.assignedServices')}
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedSpecialist.assigned_services?.map((svc, idx) => (
                    <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700">
                      {svc.service_name || svc}
                    </Badge>
                  ))}
                  {(!selectedSpecialist.assigned_services || selectedSpecialist.assigned_services.length === 0) && (
                    <span className="text-muted-foreground text-sm">{t('detail.noAssignedServices')}</span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">{t('detail.serviceCities')}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedSpecialist.service_cities?.map((city, idx) => (
                    <Badge key={idx} variant="outline">
                      <MapPin className="h-3 w-3 mr-1" />
                      {city}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('detail.maxDailyServices')}</Label>
                  <p className="font-medium text-foreground">{selectedSpecialist.max_daily_services}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.status')}</Label>
                  <div className="mt-1">
                    <Badge className={`${
                      selectedSpecialist.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                      selectedSpecialist.status === 'inactive' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' :
                      selectedSpecialist.status === 'on_leave' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                      'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300'
                    }`}>
                      {t(`status.${selectedSpecialist.status}`)}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              {t('importExport.importTitle') || 'Import Specialists'}
            </DialogTitle>
            <DialogDescription>
              {t('importExport.importDescription') || 'Upload a CSV file to import specialists. Download the template first to see the required format.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Download */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t('importExport.downloadTemplate') || 'Download Template'}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('importExport.templateHint') || 'Get the CSV format with all required fields'}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  {t('importExport.template') || 'Template'}
                </Button>
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>{t('importExport.selectFile') || 'Select CSV File'}</Label>
              <div className="flex gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="flex-1"
                />
              </div>
              {importFile && (
                <p className="text-sm text-muted-foreground">
                  {t('importExport.selectedFile') || 'Selected'}: {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-medium">{t('importExport.results') || 'Import Results'}</h4>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
                    <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-green-600">{importResults.imported}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.imported') || 'Imported'}</p>
                  </div>
                  <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.skipped') || 'Skipped'}</p>
                  </div>
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-red-600">{importResults.errors?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.errors') || 'Errors'}</p>
                  </div>
                </div>

                {importResults.errors?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-destructive mb-1">{t('importExport.errorDetails') || 'Error Details'}:</p>
                    <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                      {importResults.errors.slice(0, 10).map((err, idx) => (
                        <p key={idx} className="text-muted-foreground">
                          Row {err.row}: {err.error}
                        </p>
                      ))}
                      {importResults.errors.length > 10 && (
                        <p className="text-muted-foreground">...and {importResults.errors.length - 10} more errors</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Format Info */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium">{t('importExport.formatInfo') || 'CSV Format Information'}:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>{t('importExport.requiredFields') || 'Required: first_name, last_name, document_number, specialist_type'}</li>
                <li>{t('importExport.specialistTypes') || 'Types: doctor, nurse, physical_therapist, respiratory_therapist, occupational_therapist, lab_technician, auxiliary'}</li>
                <li>{t('importExport.multipleCities') || 'Multiple cities: separate with semicolon (;)'}</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog}>
              {t('common:cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importFile || isImporting}
              className="bg-primary hover:bg-primary/90"
            >
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('importExport.importing') || 'Importing...'}
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  {t('importExport.import') || 'Import'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpecialistsPage;
